import { useMemo, useState } from "react";
import type { CSSProperties } from "react";
import { Link } from "react-router-dom";
import { SCREENS, searchCatalog } from "@/admin/catalog";
import type { CatalogHit } from "@/admin/catalog";
import { useAdminT } from "./useAdminT";

const WRAP: CSSProperties = { position: "relative", flex: "1 1 240px", minWidth: 200 };
const INPUT: CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  padding: "8px 12px",
  borderRadius: 8,
  border: "1px solid var(--d-line)",
  background: "var(--d-ink)",
  color: "var(--d-fg)",
  fontFamily: "var(--font-body)",
  fontSize: 13,
};
const LIST: CSSProperties = {
  position: "absolute",
  top: "100%",
  left: 0,
  right: 0,
  marginTop: 4,
  maxHeight: 320,
  overflowY: "auto",
  zIndex: 10,
  listStyle: "none",
  padding: 4,
  borderRadius: 8,
  border: "1px solid var(--d-line)",
  background: "var(--d-ink)",
};
const ITEM: CSSProperties = {
  display: "block",
  padding: "8px 10px",
  borderRadius: 6,
  color: "var(--d-fg)",
  textDecoration: "none",
  fontFamily: "var(--font-body)",
  fontSize: 13,
};
const SUB: CSSProperties = { color: "var(--d-fg-muted)", fontSize: 12 };

const MAX_HITS = 12;

/** Link target: the screen route plus `?focus=<setting key | panel id>`; the screen scrolls to it. */
function hitUrl(hit: CatalogHit): string {
  const screen = SCREENS.find((s) => s.id === hit.screen);
  const focus = "key" in hit.entry ? hit.entry.key : hit.entry.id;
  return `${screen?.path ?? "/admin"}?focus=${encodeURIComponent(focus)}`;
}

export function AdminSearch(): JSX.Element {
  const t = useAdminT();
  const [query, setQuery] = useState("");
  const hits = useMemo(() => searchCatalog(query).slice(0, MAX_HITS), [query]);
  const active = query.trim().length > 0;

  return (
    <div style={WRAP}>
      <input
        type="search"
        style={INPUT}
        value={query}
        placeholder={t("shell.search.placeholder")}
        aria-label={t("shell.search.placeholder")}
        onChange={(e) => setQuery(e.target.value)}
      />
      {active && (
        <ul style={LIST} data-testid="admin-search-results">
          {hits.length === 0 ? (
            <li style={{ ...ITEM, ...SUB }}>{t("shell.search.empty")}</li>
          ) : (
            hits.map((hit) => {
              const id = "key" in hit.entry ? `s:${hit.entry.kind}:${hit.entry.key}` : `p:${hit.entry.id}`;
              return (
                <li key={id}>
                  <Link to={hitUrl(hit)} style={ITEM} onClick={() => setQuery("")}>
                    {hit.entry.name} · {SCREENS.find((s) => s.id === hit.screen)?.name}
                    <div style={SUB}>{hit.entry.description}</div>
                  </Link>
                </li>
              );
            })
          )}
        </ul>
      )}
    </div>
  );
}
